/**
 * Metrics page
 *
 * Displays Prometheus metrics from the brutal-agent.
 * Queries Prometheus directly for agent metrics.
 */

import { useEffect, useState } from 'react';
import { Card } from '../local-components';

// ═══════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════

interface PrometheusResult {
  metric: Record<string, string>;
  value: [number, string]; // [timestamp, value]
}

interface MetricData {
  name: string;
  value: number;
  labels: Record<string, string>;
  timestamp: Date;
}

// ═══════════════════════════════════════════════════════════════
// METRIC CARD
// ═══════════════════════════════════════════════════════════════

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle: string | null;
  color: string | null;
}

function MetricCard({ title, value, subtitle, color }: MetricCardProps) {
  return (
    <div
      style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '8px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
      }}
    >
      <span style={{ color: '#888', fontSize: '0.9rem' }}>{title}</span>
      <span
        style={{
          fontSize: '2rem',
          fontWeight: 'bold',
          color: color ?? '#00ff88',
        }}
      >
        {value}
      </span>
      {subtitle !== null && <span style={{ color: '#666', fontSize: '0.8rem' }}>{subtitle}</span>}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// CAPABILITY TABLE
// ═══════════════════════════════════════════════════════════════

interface CapabilityMetrics {
  method: string;
  successCount: number;
  errorCount: number;
  avgDurationMs: number;
}

function CapabilityTable({ metrics }: { metrics: CapabilityMetrics[] }) {
  if (metrics.length === 0) {
    return <p style={{ color: '#666' }}>No capability metrics yet.</p>;
  }

  return (
    <div
      style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '8px',
        overflow: 'hidden',
      }}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #333' }}>
            <th
              style={{
                textAlign: 'left',
                padding: '12px 16px',
                color: '#888',
                fontWeight: 'normal',
              }}
            >
              Method
            </th>
            <th
              style={{
                textAlign: 'right',
                padding: '12px 16px',
                color: '#888',
                fontWeight: 'normal',
              }}
            >
              Success
            </th>
            <th
              style={{
                textAlign: 'right',
                padding: '12px 16px',
                color: '#888',
                fontWeight: 'normal',
              }}
            >
              Errors
            </th>
            <th
              style={{
                textAlign: 'right',
                padding: '12px 16px',
                color: '#888',
                fontWeight: 'normal',
              }}
            >
              Avg Duration
            </th>
          </tr>
        </thead>
        <tbody>
          {metrics.map((m) => (
            <tr key={m.method} style={{ borderBottom: '1px solid #222' }}>
              <td style={{ padding: '12px 16px', fontFamily: 'monospace' }}>{m.method}</td>
              <td style={{ padding: '12px 16px', textAlign: 'right', color: '#00ff88' }}>
                {m.successCount}
              </td>
              <td
                style={{
                  padding: '12px 16px',
                  textAlign: 'right',
                  color: m.errorCount > 0 ? '#ff4444' : '#666',
                }}
              >
                {m.errorCount}
              </td>
              <td style={{ padding: '12px 16px', textAlign: 'right', color: '#888' }}>
                {m.avgDurationMs.toFixed(1)}ms
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// METRICS PAGE
// ═══════════════════════════════════════════════════════════════

// Prometheus endpoint - query via agent's host
const PROMETHEUS_URL = '/api/prometheus';

export function Metrics() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<MetricData[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Fetch metrics from Prometheus
  const fetchMetrics = async () => {
    try {
      // Query all brutal_* metrics
      const queries = [
        'brutal_agent_startup',
        'brutal_capability_invocations_total',
        'brutal_capability_duration_ms',
      ];

      const results: MetricData[] = [];

      for (const query of queries) {
        try {
          const response = await fetch(
            `${PROMETHEUS_URL}/api/v1/query?query=${encodeURIComponent(query)}`,
          );
          if (!response.ok) continue;

          const data = await response.json();
          if (data.status !== 'success') continue;

          for (const result of data.data.result as PrometheusResult[]) {
            results.push({
              name: result.metric['__name__'] ?? query,
              value: parseFloat(result.value[1]),
              labels: result.metric,
              timestamp: new Date(result.value[0] * 1000),
            });
          }
        } catch {
          // Individual query failed, continue with others
        }
      }

      setMetrics(results);
      setLastUpdated(new Date());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    const interval = setInterval(fetchMetrics, 15000); // Refresh every 15s
    return () => clearInterval(interval);
  }, []);

  // Process metrics into summary data
  const agentStartup = metrics.find((m) => m.name === 'brutal_agent_startup');
  const isAgentUp = agentStartup !== undefined && agentStartup.value === 1;

  // Group capability metrics by method
  const capabilityMetricsMap = new Map<string, CapabilityMetrics>();

  for (const m of metrics) {
    if (m.name === 'brutal_capability_invocations_total') {
      const method = m.labels['method'] ?? 'unknown';
      const status = m.labels['status'] ?? 'unknown';

      if (!capabilityMetricsMap.has(method)) {
        capabilityMetricsMap.set(method, {
          method,
          successCount: 0,
          errorCount: 0,
          avgDurationMs: 0,
        });
      }

      const cap = capabilityMetricsMap.get(method)!;
      if (status === 'success') {
        cap.successCount = m.value;
      } else if (status === 'error') {
        cap.errorCount = m.value;
      }
    }

    if (m.name === 'brutal_capability_duration_ms') {
      const method = m.labels['method'] ?? 'unknown';

      if (!capabilityMetricsMap.has(method)) {
        capabilityMetricsMap.set(method, {
          method,
          successCount: 0,
          errorCount: 0,
          avgDurationMs: 0,
        });
      }

      const cap = capabilityMetricsMap.get(method)!;
      cap.avgDurationMs = m.value;
    }
  }

  const capabilityMetrics = Array.from(capabilityMetricsMap.values()).sort(
    (a, b) => b.successCount + b.errorCount - (a.successCount + a.errorCount),
  );

  // Calculate totals
  const totalInvocations = capabilityMetrics.reduce(
    (sum, m) => sum + m.successCount + m.errorCount,
    0,
  );
  const totalErrors = capabilityMetrics.reduce((sum, m) => sum + m.errorCount, 0);
  const errorRate =
    totalInvocations > 0 ? ((totalErrors / totalInvocations) * 100).toFixed(1) : '0.0';

  if (loading) {
    return (
      <div>
        <h2 style={{ fontSize: '2rem', marginBottom: '20px' }}>Metrics</h2>
        <p style={{ color: '#888' }}>Loading metrics...</p>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
        }}
      >
        <h2 style={{ fontSize: '2rem', margin: 0 }}>Metrics</h2>
        {lastUpdated !== null && (
          <span style={{ color: '#666', fontSize: '0.85rem' }}>
            Last updated: {lastUpdated.toLocaleTimeString()}
          </span>
        )}
      </div>

      {error !== null ? (
        <Card variant="outline" padding="md" hover={null}>
          <p style={{ color: '#ff4444' }}>Error: {error}</p>
          <p style={{ color: '#888', fontSize: '0.9rem', marginTop: '8px' }}>
            Make sure Prometheus is accessible at the configured endpoint.
          </p>
        </Card>
      ) : (
        <>
          {/* Overview Stats */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(4, 1fr)',
              gap: '20px',
              marginBottom: '32px',
            }}
          >
            <MetricCard
              title="Agent Status"
              value={isAgentUp ? 'UP' : 'DOWN'}
              subtitle={agentStartup?.labels['mode'] ?? null}
              color={isAgentUp ? '#00ff88' : '#ff4444'}
            />
            <MetricCard
              title="Total Invocations"
              value={totalInvocations}
              subtitle="all capabilities"
              color={null}
            />
            <MetricCard
              title="Total Errors"
              value={totalErrors}
              subtitle={`${errorRate}% error rate`}
              color={totalErrors > 0 ? '#ff4444' : '#00ff88'}
            />
            <MetricCard
              title="Capabilities"
              value={capabilityMetrics.length}
              subtitle="active methods"
              color={null}
            />
          </div>

          {/* Capability Breakdown */}
          <div>
            <h3 style={{ fontSize: '1.25rem', marginBottom: '16px', color: '#888' }}>
              Capability Metrics
            </h3>
            <CapabilityTable metrics={capabilityMetrics} />
          </div>

          {/* Raw Metrics (Debug) */}
          <details style={{ marginTop: '32px' }}>
            <summary
              style={{
                color: '#666',
                cursor: 'pointer',
                padding: '8px 0',
              }}
            >
              Raw Metrics ({metrics.length})
            </summary>
            <pre
              style={{
                backgroundColor: '#1a1a1a',
                padding: '16px',
                borderRadius: '8px',
                overflow: 'auto',
                fontSize: '0.8rem',
                color: '#888',
                marginTop: '8px',
              }}
            >
              {JSON.stringify(metrics, null, 2)}
            </pre>
          </details>
        </>
      )}
    </div>
  );
}
